/* eslint-disable react/no-danger */
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  DeleteOutlined, PlusOutlined,
  PrinterOutlined,
  SaveOutlined,
  ScanOutlined
} from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AutoComplete,
  Button, Card, Col,
  message,
  Modal,
  Row, Space, Spin, Switch, Tooltip, Typography
} from 'antd';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import React, {
  forwardRef, useEffect, useImperativeHandle, useMemo
} from 'react';
import {
  Controller, FormProvider, useFieldArray, useForm
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { Picker } from '../Picker';

import { useAppSelector } from 'app/store';
import { DropdownElement } from 'common/components/DropdownType';
import HeaderPage from 'common/components/HeaderPage';
import Input from 'common/components/Input';
import UploadPhoto, { UploadFileType } from 'common/components/UploadPhoto';
import { getAllBankService, getProvinceByBankNameService } from 'common/services/extends/banks';
import { BankDataTypes, ProvinceByBankItemTypes } from 'common/services/extends/banks/types';
import { PayrollPeriodsDataTypes } from 'common/services/extends/payrollPeriods/types';
import { checkStaffByIdCardProjectService } from 'common/services/extends/project';
import {
  createStaffService, getInfoByExistedIdCard, ocrStaffInfoService, printStaffService, updateStaffService
} from 'common/services/extends/staff';
import { StaffContractEnum, StaffRequestType, StaffResponseType } from 'common/services/extends/staff/types';
import { StaffAssignDetailResponseType } from 'common/services/extends/staffAssign/types';
import { contractTypeOptions, NoTaxCode, ROUTE_PATHS } from 'common/utils/constant';
import { detectErrorByCode, formatDateYYYYMMDD, removeVietnameseDiacriticsAndUppercase } from 'common/utils/functions';
import { createDetailStaffSchema, updateDetailStaffSchema } from 'common/utils/schemas';
import roles, { getPermission } from 'configs/roles';
import { BankAccount, TableDataTypes } from 'extends/Employee';

export type CreateStaffRef = {
  handleReset(): void
};
export type StaffFormReturnTypes = Omit<TableDataTypes, 'id' | 'socialInsuranceImage'> & {
  socialInsuranceImage?: UploadFileType;
  socialInsuranceImagePath?: string;
};

export type CreateEmployeeProps = {
  data?: StaffResponseType;
  loading?: boolean;
  program?: {
    loading?: boolean;
    info: {
      name: string
      code: string
      customerName: string
      payrollName: string
    }
    handleClosePopup?: (idCardNumber: string) => void;
    handleSubmit?: () => void;
  }
  payrollData?: PayrollPeriodsDataTypes
} & ActiveRolesExpend;

type FormReturnTypes = Omit<TableDataTypes, 'id' | 'socialInsuranceImage' | 'bankAccounts'> & {
  socialInsuranceImage?: UploadFileType;
  socialInsuranceImagePath?: string;
  idCardFrontImage?: UploadFileType;
  idCardFrontPath?: string;
  idCardBackImage?: UploadFileType;
  idCardBackPath?: string;
  bankAccounts: Array<BankAccount & { bankId?: number, isDisabled?: boolean }>
};

const defaultValues: FormReturnTypes = {
  fullname: '',
  taxCode: '',
  phoneNumber: '',
  idCardNumber: '',
  idCardIssuedAt: undefined,
  idCardIssuedBy: '',
  permanentAddress: '',
  contractType: {
    label: StaffContractEnum[1],
    value: StaffContractEnum['Part-time'],
  },
  dob: undefined,
  socialInsuranceNumber: '',
  socialInsuranceJoinDate: undefined,
  socialInsuranceImage: undefined,
  socialInsuranceImagePath: '',
  idCardFrontImage: undefined,
  idCardFrontPath: '',
  idCardBackImage: undefined,
  idCardBackPath: '',
  bankAccounts: []
};

const CreateEmployee = forwardRef<CreateStaffRef, CreateEmployeeProps>(({
  roleUpdate, roleOther, loading = false, data, program, roleCreate,
  payrollData,
}, ref) => {
  /* Hooks */
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { roles: userRoles } = useAppSelector((state) => state.auth);

  /* Search params */
  const [searchParams] = useSearchParams();
  const idParams = Number(searchParams.get('id'));
  const payrollPeriodId = searchParams.get('payrollPeriodId') || '';

  /* States */
  const [watchIndex, setWatchIndex] = React.useState<number>(-1);
  const [viewOnly, setViewOnly] = React.useState<boolean>(false);
  const [existedInfo, setExistedInfo] = React.useState<{ idNumber: string, info: StaffResponseType | StaffAssignDetailResponseType | null } | null>(null);
  const [contractTypeState, setContractTypeState] = React.useState<StaffContractEnum>(StaffContractEnum['Part-time']);
  const [isAddStaffProgram, setIsAddStaffProgram] = React.useState<boolean>(false);
  const [hasCheckedIdCard, setHasCheckedIdCard] = React.useState<boolean>(false);
  const [isNotMatchIdCardNumber, setIsNotMatchIdCardNumber] = React.useState<boolean>(false);

  /* Check permission */
  const canAccessOCR = getPermission(userRoles, roles.EMPLOYEE_OCR) || userRoles.includes('*');
  const deleteBankRole = getPermission(userRoles, roles.EMPLOYEE_DELETE_BANK);

  /* React-hook-form */
  const method = useForm<FormReturnTypes>({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(idParams ? updateDetailStaffSchema : createDetailStaffSchema)
  });

  const idCardNumberWatch = method.watch('idCardNumber');
  const errorIdCardNumber = method.getFieldState('idCardNumber').error;

  const cccdValid = useMemo(() => {
    if (!errorIdCardNumber && idCardNumberWatch.length === 12) {
      return false;
    }
    return true;
  }, [errorIdCardNumber, idCardNumberWatch]);

  const errorState = method.formState.errors as unknown as {
    [x in keyof FormReturnTypes]: { message: string }
  };

  /* Use imperative handler */
  useImperativeHandle(ref, () => ({
    handleReset: () => {
      method.reset(defaultValues);
      setViewOnly(false);
      setIsAddStaffProgram(false);
      setExistedInfo(null);
      setHasCheckedIdCard(false);
    }, // expose reset ra ngoài
  }));

  /* Queries */
  const initBankNames = data?.bankData.map((bank) => bank.bankName) || [];

  // Watch front/back id card path
  const {
    idCardFrontImage: isDirtyFrontIdCard,
    idCardBackImage: isDirtyBackIdCard
  } = method.formState.dirtyFields;

  const frontIdentityPath = isDirtyFrontIdCard ? method.watch('idCardFrontImage')?.pathSubmit || '' : (data?.staffData.frontIdCardPath || '');
  const backIdentityPath = isDirtyBackIdCard ? method.watch('idCardBackImage')?.pathSubmit || '' : (data?.staffData?.backIdCardPath || '');
  const { isDirty: isDirtyIdCardFrontImage } = method.getFieldState('idCardFrontImage');
  const { isDirty: isDirtyIdCardBackImage } = method.getFieldState('idCardBackImage');

  // Watch bank item idx change
  const bankNameWatch = method.watch(`bankAccounts.${watchIndex}.bankName`);
  const cityWatch = method.watch(`bankAccounts.${watchIndex}.city`);
  const bankAccountsWatch = method.watch('bankAccounts') || [];

  // Check bank name to disabled city, branch bank
  const isNotAgribank = (index: number) => !!(bankAccountsWatch[index]?.bankName?.value && !bankAccountsWatch[index]?.bankName?.value?.toString().toLowerCase().includes('agribank'));
  const bankAccountIdx = bankAccountsWatch?.map((_, index) => index);
  const disabledBankField = (index: number) => isNotAgribank(index) && bankAccountIdx.includes(index);

  const onToggleDefault = (e: boolean, index: number) => {
    const isActive = method.getValues(`bankAccounts.${index}.isActive`);
    if (!isActive) method.setValue(`bankAccounts.${index}.isActive`, e);

    // Toggle off other bank accounts
    const accounts = method.getValues('bankAccounts');
    accounts.forEach((acc, idx) => {
      if (idx !== index && acc.isDefault) {
        method.setValue(`bankAccounts.${idx}.isDefault`, false);
      }
    });

    // Clear error after changed
    if (e && method.formState.errors.bankAccounts) {
      method.clearErrors('bankAccounts');
    }
  };

  const { isLoading: isLoadingInfoFromOCR, mutate: mutateInfoFromOCR } = useMutation(
    ['getInfoFromOCR', frontIdentityPath, backIdentityPath],
    () => ocrStaffInfoService({
      backIdCardPath: backIdentityPath,
      frontIdCardPath: frontIdentityPath,
    }),
    {
      onSuccess: (res) => {
        if (res) {
          const valueForm = method.getValues();
          const {
            idCardAddress, idCardDob, idCardIssuedAt, idCardIssuedBy, idCardName, idCardNumber
          } = res.data;
          if (!!idParams && idCardNumber !== valueForm.idCardNumber) {
            setIsNotMatchIdCardNumber(true);
          } else {
            method.reset({
              ...valueForm,
              permanentAddress: idCardAddress || valueForm.permanentAddress,
              dob: idCardDob || valueForm.dob,
              fullname: idCardName || valueForm.fullname,
              idCardIssuedAt: idCardIssuedAt || valueForm.idCardIssuedAt,
              idCardIssuedBy: idCardIssuedBy || valueForm.idCardIssuedBy,
              idCardNumber,
            });
          }
        }
      },
      onError: (errors: any) => {
        if (errors && Array.isArray(errors) && errors.length > 0) {
          errors.forEach((item) => {
            method.setError('idCardFrontImage', {
              message: t(`${detectErrorByCode(item.code)}`),
              type: 'validate'
            });
          });
        }
      },
    }
  );

  useQuery(
    ['get-bank-name-list', watchIndex, isNotAgribank(watchIndex)],
    () => getAllBankService({
      page: 1,
      ...bankNameWatch && { _name: bankNameWatch.value?.toString() },
    }),
    {
      enabled: watchIndex !== -1 && isNotAgribank(watchIndex),
      onSuccess: (res) => {
        if (res.data[0]?.bankData) {
          method.setValue(`bankAccounts.${watchIndex}.bankId`, res.data[0].bankData.id);
        }
      }
    }
  );

  const {
    data: cityRes,
    hasNextPage,
    fetchNextPage,
    isLoading: getCityLoading,
    isFetching: isFetchingCity
  } = useInfiniteQuery(
    ['get-city-list-by-bank', bankNameWatch, isNotAgribank(watchIndex)],
    ({ pageParam }) => getProvinceByBankNameService({
      page: pageParam,
      limit: 15,
      ...bankNameWatch && { _name: bankNameWatch.value?.toString() },
    }),
    {
      enabled: !!bankNameWatch?.value && !isNotAgribank(watchIndex) && roleOther?.includes(roles.PROVINCE_INDEX),
      getNextPageParam: (lastPage) => {
        if (lastPage.meta.page >= lastPage.meta.totalPages) return undefined;
        return lastPage.meta.page + 1;
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          message.error(
            error.response?.data?.message || t('message.anErrorHasOccurred')
          );
        }
      },
    }
  );

  const cityListOption: OptionType[] = useMemo(() => (cityRes?.pages.reduce((acc, page) => {
    const newAcc = [...acc, ...page.data];
    return newAcc;
  }, [] as ProvinceByBankItemTypes[]) || []).map((b) => ({
    label: b.province?.name,
    value: b.province?.code
  })), [cityRes]);

  const fetchMoreCityList = () => {
    if (hasNextPage) fetchNextPage();
  };

  const {
    data: branchRes,
    hasNextPage: hasNextBranchPage,
    fetchNextPage: fetchNextBranchPage,
    isLoading: getBranchLoading,
    isFetching: isFetchingBranch
  } = useInfiniteQuery(
    ['get-branch-list-by-bank', cityWatch],
    ({ pageParam }) => getAllBankService({
      page: pageParam,
      limit: 15,
      ...bankNameWatch && { _name: bankNameWatch.value?.toString() },
      ...cityWatch && { _provinceCode: cityWatch.value?.toString() },
    }),
    {
      enabled: !!cityWatch && roleOther?.includes(roles.BANK_INDEX),
      getNextPageParam: (lastPage) => {
        if (lastPage.meta.page >= lastPage.meta.totalPages) return undefined;
        return lastPage.meta.page + 1;
      },
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          message.error(
            error.response?.data?.message || t('message.anErrorHasOccurred')
          );
        }
      },
    }
  );

  const branchListOption: (OptionType & { bankId: number })[] = useMemo(() => (branchRes?.pages.reduce((acc, page) => {
    const newAcc = [...acc, ...page.data];
    return newAcc;
  }, [] as BankDataTypes[]) || []).map((b) => ({
    label: b.bankData.branchName,
    value: b.bankData.id,
    bankId: b.bankData.id
  })), [branchRes]);

  const fetchMoreBranchList = () => {
    if (hasNextBranchPage) fetchNextBranchPage();
  };

  const { mutate: createMutate, isLoading: createLoading } = useMutation(
    ['create-staff'],
    createStaffService,
    {
      onSuccess: (_, params) => {
        if (program && program.handleClosePopup) {
          program.handleClosePopup(params.idCardNumber);
        } else {
          navigate(ROUTE_PATHS.EMPLOYEE_MANAGEMENT);
        }
        message.success(t('message.createSuccess'));
      },
      onError: (error: ErrorResponse[]) => {
        if (error.length > 0) {
          error.forEach((
            e
          ) => {
            method.setError(
              e.field as keyof FormReturnTypes,
              { message: t(detectErrorByCode(e.code, e.field, e.message)) }
            );
            message.error(
              t(detectErrorByCode(e.code, e.field, e.message))
            );
          });
        } else {
          message.error(t('message.anErrorHasOccurred'));
        }
      }
    }
  );

  const { mutate: updateMutate, isLoading: updateLoading } = useMutation(
    ['update-staff'],
    updateStaffService,
    {
      onSuccess: () => {
        message.success(t('message.updateSuccess'));
      },
      onError: (error: ErrorResponse[]) => {
        if (error.length > 0) {
          error.forEach((
            e
          ) => {
            method.setError(
              e.field as keyof FormReturnTypes,
              { message: t(detectErrorByCode(e.code, e.field, e.message)) }
            );
            message.error(
              t(detectErrorByCode(e.code, e.field, e.message))
            );
          });
        } else {
          message.error(t('message.anErrorHasOccurred'));
        }
      }
    }
  );

  const { fields, append, remove } = useFieldArray({
    control: method.control,
    name: 'bankAccounts',
  });

  /* Functions */
  const onSubmit = async () => {
    const isValid = await method.trigger();
    if (!isValid) {
      return;
    }

    const formData = method.getValues();
    const {
      fullname, dob, taxCode, idCardNumber,
      idCardIssuedAt, idCardIssuedBy, permanentAddress,
      contractType,
      socialInsuranceNumber,
      socialInsuranceJoinDate,
      socialInsuranceImage,
      socialInsuranceImagePath,
      idCardFrontImage, idCardFrontPath,
      idCardBackImage, idCardBackPath,
      phoneNumber
    } = formData;
    const reqObj: StaffRequestType = {
      fullname,
      dob: formatDateYYYYMMDD(dob),
      taxCode: taxCode === NoTaxCode.value ? '' : taxCode,
      phoneNumber,
      idCardNumber,
      idCardIssuedAt: formatDateYYYYMMDD(idCardIssuedAt),
      idCardIssuedBy: idCardIssuedBy || '',
      permanentAddress: permanentAddress || '',
      contractType: Number(contractType?.value),
      socialInsuranceImage: socialInsuranceImage?.file ? socialInsuranceImage?.pathSubmit : socialInsuranceImagePath || '',
      socialInsuranceNumber: socialInsuranceNumber || '',
      socialInsuranceJoinDate: formatDateYYYYMMDD(socialInsuranceJoinDate),
      banks: formData.bankAccounts.map((bank) => ({
        accountName: bank.bankOwner,
        accountNumber: bank.bankAccount,
        accountDefault: bank.isDefault,
        isActive: !!bank.isActive,
        bankId: bank.bankId || 0,
      })),
      frontIdCard: idCardFrontImage?.file ? idCardFrontImage?.pathSubmit : idCardFrontPath || '',
      backIdCard: idCardBackImage?.file ? idCardBackImage?.pathSubmit : idCardBackPath || '',
    };
    if (idParams) {
      updateMutate({
        id: idParams,
        data: reqObj
      });
    } else {
      createMutate(reqObj);
    }
  };

  const { mutate: getInfoMutate, isLoading: getInfoByIdCardLoading } = useMutation(
    ['get-info-by-idCard'],
    getInfoByExistedIdCard,
    {
      onSuccess(res, param) {
        setExistedInfo({ idNumber: param, info: res });
        if (res) setContractTypeState((res as StaffResponseType).staffData ? StaffContractEnum['Part-time'] : StaffContractEnum.HDGV);
      },
      onError: (error: ErrorResponse[]) => {
        if (error.length > 0) {
          error.forEach((
            e
          ) => {
            method.setError(
              e.field as keyof FormReturnTypes || 'idCardNumber',
              { message: t(detectErrorByCode(e.code, e.field, e.message)) }
            );
            message.error(
              t(detectErrorByCode(e.code, e.field, e.message))
            );
          });
        } else {
          message.error(t('message.anErrorHasOccurred'));
        }
      }
    }
  );

  const { mutate: checkStaffByIdCardProjectMutate, isLoading: checkStaffByIdCardProjectLoading } = useMutation(
    ['check-info-staff-in-project-by-idCard'],
    (
      params: {
        payrollPeriodId: string,
        params: { idCardNumber: string }
      }
    ) => checkStaffByIdCardProjectService(params.payrollPeriodId, params.params),
    {
      onSuccess(res, { params: { idCardNumber } }) {
        setExistedInfo({ idNumber: idCardNumber, info: res });
        if (res) {
          setContractTypeState((res as StaffResponseType).staffData ? StaffContractEnum['Part-time'] : StaffContractEnum.HDGV);
          setIsAddStaffProgram(true);
        }
      },
      onError: (error: ErrorResponse[]) => {
        if (error.length > 0) {
          error.forEach((
            e
          ) => {
            method.setError(
              e.field as keyof FormReturnTypes || 'idCardNumber',
              { message: t(detectErrorByCode(e.code, e.field, e.message)) }
            );
            message.error(
              t(detectErrorByCode(e.code, e.field, e.message))
            );
          });
        } else {
          message.error(t('message.anErrorHasOccurred'));
        }
      }
    }
  );

  const onReuseInfo = () => {
    if (existedInfo?.info) {
      const {
        fullname,
        dob,
        idCardIssuedAt,
        idCardIssuedBy,
        idCardNumber,
        permanentAddress,
        socialInsuranceImage,
        socialInsuranceImagePath,
        socialInsuranceJoinDate,
        socialInsuranceNumber,
        taxCode,
        backIdCardPath,
        backIdCard,
        frontIdCard,
        frontIdCardPath,
        phoneNumber,
        contractType
      } = (existedInfo.info as StaffResponseType).staffData || (existedInfo.info as StaffAssignDetailResponseType).staffAssignData;
      method.reset({
        fullname,
        taxCode: taxCode === '' ? NoTaxCode.value : taxCode,
        idCardNumber,
        idCardIssuedBy,
        permanentAddress,
        socialInsuranceNumber,
        dob,
        idCardIssuedAt,
        contractType: {
          label: StaffContractEnum[contractType],
          value: contractType,
        },
        socialInsuranceJoinDate,
        socialInsuranceImage: {
          file: undefined,
          pathSubmit: socialInsuranceImage || '',
        },
        socialInsuranceImagePath: socialInsuranceImagePath || '',
        idCardFrontPath: frontIdCardPath || '',
        idCardFrontImage: {
          file: undefined,
          pathSubmit: frontIdCard || '',
        },
        idCardBackPath: backIdCardPath || '',
        idCardBackImage: {
          file: undefined,
          pathSubmit: backIdCard || '',
        },
        phoneNumber,
        ...(existedInfo.info as StaffResponseType).bankData?.length > 0 && {
          bankAccounts: (existedInfo.info as StaffResponseType).bankData?.map((bank) => ({
            bankOwner: removeVietnameseDiacriticsAndUppercase(fullname),
            bankAccount: bank.accountNumber,
            isDefault: bank.accountDefault,
            isActive: bank.isActive,
            bankName: {
              value: bank.bankName,
              label: bank.bankName
            },
            bankBranch: {
              value: bank.branchName,
              label: bank.branchName
            },
            city: {
              value: bank.province?.code,
              label: bank.province?.name
            },
            bankId: bank.bankId,
            isDisabled: true // disable when update
          })),
        }
        ,
      });
      setExistedInfo(null);
      setViewOnly(true);
    } else {
      setExistedInfo(null);
      method.reset({
        ...defaultValues,
        idCardNumber: method.getValues('idCardNumber')
      });
    }
    setHasCheckedIdCard(true);
  };

  const handleRemove = async (indexToRemove: number) => {
    await remove(indexToRemove);

    const accounts = method.getValues('bankAccounts');
    const hasDefault = accounts.some((b) => b.isDefault);

    if (!hasDefault && accounts.length > 0) {
      const lastIndex = accounts.length - 1;
      method.setValue(`bankAccounts.${lastIndex}.isDefault`, true);
    }
  };

  const renderReuseMessage = useMemo(() => {
    let reuseMessage = t('employee.idCardNotExist');
    if (existedInfo?.info) {
      if (contractTypeState === StaffContractEnum['Part-time']) reuseMessage = t('employee.idCardIsExistedStaff');
      if (contractTypeState === StaffContractEnum.HDGV) reuseMessage = t('employee.idCardIsExistedStaffAssign');
    }
    return reuseMessage;
  }, [existedInfo?.info, contractTypeState, t]);

  useEffect(() => {
    if (data) {
      const {
        fullname,
        dob,
        contractType,
        idCardIssuedAt,
        idCardIssuedBy,
        idCardNumber,
        permanentAddress,
        socialInsuranceImage,
        socialInsuranceImagePath,
        socialInsuranceJoinDate,
        socialInsuranceNumber,
        taxCode,
        backIdCardPath,
        backIdCard,
        frontIdCard,
        frontIdCardPath,
        phoneNumber
      } = data.staffData;

      method.reset({
        fullname,
        taxCode: taxCode === '' ? NoTaxCode.value : taxCode,
        idCardNumber,
        idCardIssuedBy,
        permanentAddress,
        socialInsuranceNumber,
        dob,
        idCardIssuedAt,
        contractType: {
          value: contractType,
          label: StaffContractEnum[contractType]
        },
        socialInsuranceJoinDate,
        socialInsuranceImage: {
          file: undefined,
          pathSubmit: socialInsuranceImage || '',
        },
        bankAccounts: data.bankData.map((bank) => ({
          bankOwner: removeVietnameseDiacriticsAndUppercase(fullname),
          bankAccount: bank.accountNumber,
          isDefault: bank.accountDefault,
          isActive: bank.isActive,
          bankName: {
            value: bank.bankName,
            label: bank.bankName
          },
          bankBranch: {
            value: bank.branchName,
            label: bank.branchName
          },
          city: {
            value: bank.province?.code,
            label: bank.province?.name
          },
          bankId: bank.bankId,
          isDisabled: true // disable when update
        })),
        socialInsuranceImagePath: socialInsuranceImagePath || '',
        idCardFrontPath: frontIdCardPath || '',
        idCardFrontImage: {
          file: undefined,
          pathSubmit: frontIdCard || '',
        },
        idCardBackPath: backIdCardPath || '',
        idCardBackImage: {
          file: undefined,
          pathSubmit: backIdCard || '',
        },
        phoneNumber
      });
    }
  }, [data]);

  const { mutate: printMuate, isLoading: isLoadingPrint } = useMutation(
    ['print-employee'],
    (id: number) => printStaffService(id),
    {
      onSuccess: (res) => {
        window.open(res.link, '_blank');
        message.success(t('message.exportSuccess'));
      },
      onError: () => {
        message.error(t('message.exportError'));
      }
    }
  );

  const onHandlePrint = (id: number) => {
    printMuate(id);
  };

  /* Render */
  return (
    <>
      {
        !program && (
          <HeaderPage
            fixed
            title={t('sidebar.employee')}
            rightHeader={(
              <Space size={16}>
                {
                  roleOther?.includes(roles.PROJECT_PRINT_CONTRACT) && (
                    <Button
                      disabled={!roleOther?.includes(roles.PROJECT_PRINT_CONTRACT) || isLoadingPrint}
                      icon={<PrinterOutlined />}
                      onClick={() => onHandlePrint(idParams)}
                    >
                      <Typography.Text>
                        {t('employee.printContract')}
                      </Typography.Text>
                    </Button>
                  )
                }
                <Button
                  type="primary"
                  disabled={(Boolean(idParams) && !roleUpdate) || viewOnly || (!hasCheckedIdCard && !idParams)}
                  onClick={method.handleSubmit(onSubmit)}
                >
                  <SaveOutlined />
                  {t('system.save')}
                </Button>
              </Space>
            )}
          />
        )
      }
      <Spin size="large" spinning={loading || updateLoading || createLoading || isLoadingInfoFromOCR}>
        <div className="t-mainlayout_wrapper">
          <FormProvider {...method}>
            <Row gutter={16}>
              <Col xxl={24}>
                <Card type="inner">
                  <div className="site-card-border-less-wrapper">
                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                      {program && (
                        <Card
                          title={<Typography.Title level={4}>{t('timesheet.infoProgram')}</Typography.Title>}
                        >
                          <Row gutter={16}>
                            <Col sm={12} xl={6} className="u-mb-8">
                              <Typography.Text>
                                {t('program.code')}
                                {': '}
                              </Typography.Text>
                              <div>
                                <Typography.Text strong>
                                  {program.info.code}
                                </Typography.Text>
                              </div>
                            </Col>
                            <Col sm={12} xl={6} className="u-mb-8">
                              <Typography.Text>
                                {t('program.nameCustomer')}
                                {': '}
                              </Typography.Text>
                              <div>
                                <Typography.Text strong>
                                  {program.info.customerName}
                                </Typography.Text>
                              </div>
                            </Col>
                            <Col sm={12} xl={6} className="u-mb-8">
                              <Typography.Text>
                                {t('program.name')}
                                {': '}
                              </Typography.Text>
                              <div>
                                <Typography.Text strong>
                                  {program.info.name}
                                </Typography.Text>
                              </div>
                            </Col>
                            <Col sm={12} xl={6} className="u-mb-8">
                              <Typography.Text>
                                {t('program.creatorTableSalary')}
                                {': '}
                              </Typography.Text>
                              <div>
                                <Typography.Text strong>
                                  {program.info.payrollName}
                                </Typography.Text>
                              </div>
                            </Col>
                          </Row>
                          <Row gutter={16}>
                            <Col sm={12} xl={6} className="u-mt-8">
                              <Typography.Text>
                                {t('timesheet.timeKeepingName')}
                                {': '}
                              </Typography.Text>
                              <div>
                                <Typography.Text strong>
                                  {payrollData?.payrollPeriodData?.name}
                                </Typography.Text>
                              </div>
                            </Col>
                            <Col sm={12} xl={6} className="u-mt-8">
                              <Typography.Text>
                                {t('timesheet.fromDate')}
                                {': '}
                              </Typography.Text>
                              <div>
                                <Typography.Text strong>
                                  {payrollData?.payrollPeriodData?.startDate ? dayjs(payrollData?.payrollPeriodData?.startDate).format('DD/MM/YYYY') : ''}
                                </Typography.Text>
                              </div>
                            </Col>
                            <Col sm={12} xl={6} className="u-mt-8">
                              <Typography.Text>
                                {t('timesheet.endDate')}
                                {': '}
                              </Typography.Text>
                              <div>
                                <Typography.Text strong>
                                  {payrollData?.payrollPeriodData?.endDate ? dayjs(payrollData?.payrollPeriodData?.endDate).format('DD/MM/YYYY') : ''}
                                </Typography.Text>
                              </div>
                            </Col>
                          </Row>
                        </Card>
                      )}
                      <Card
                        title={<Typography.Title level={4}>{t('employee.information')}</Typography.Title>}
                        className="u-mt-16"
                      // extra={(
                      //   <>
                      //     <Button
                      //       type="primary"
                      //       onClick={() => { }}
                      //       icon={<CameraOutlined />}
                      //       style={{ marginRight: 8 }}
                      //     >
                      //       {t('employee.takePhoto')}
                      //     </Button>
                      //     <Button
                      //       type="primary"
                      //       onClick={() => { }}
                      //       icon={<CloudUploadOutlined />}
                      //     >
                      //       {t('employee.uploadImg')}
                      //     </Button>
                      //   </>
                      // )}
                      >
                        <Row gutter={16}>
                          <Col span={8} className="u-mb-8">
                            <Typography.Text strong>
                              {t('employee.identityNumber')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="idCardNumber"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Space className="idCardNumberInput" style={{ width: '100%', alignItems: 'flex-start' }}>
                                  <Input
                                    className="u-mt-8"
                                    value={value}
                                    onChange={(e) => {
                                      onChange(e.target.value);
                                      if (hasCheckedIdCard) setHasCheckedIdCard(false);
                                    }}
                                    error={error?.message}
                                    size="large"
                                    disabled={!!idParams || viewOnly}
                                  />
                                  <Tooltip title={getPermission(userRoles, roles.EMPLOYEE_FIND_BY_ID_CARD_NUMBER) ? '' : 'Bạn chưa có quyền kiểm tra CCCD'}>
                                    <Button
                                      loading={getInfoByIdCardLoading}
                                      size="large"
                                      type="primary"
                                      className="u-mt-8"
                                      onClick={() => {
                                        if (viewOnly) {
                                          setViewOnly(false);
                                          setIsAddStaffProgram(false);
                                          setHasCheckedIdCard(false);
                                          method.reset({
                                            ...defaultValues,
                                          });
                                        } else if (program) {
                                          checkStaffByIdCardProjectMutate({
                                            payrollPeriodId,
                                            params: {
                                              idCardNumber: value
                                            }
                                          });
                                        } else {
                                          getInfoMutate(value);
                                        }
                                      }}
                                      disabled={!getPermission(userRoles, roles.EMPLOYEE_FIND_BY_ID_CARD_NUMBER) || !!idParams || cccdValid || checkStaffByIdCardProjectLoading}
                                    >
                                      {viewOnly ? (
                                        t('employee.editIdCard')
                                      ) : (
                                        t('employee.checkIdCard')
                                      )}
                                    </Button>
                                  </Tooltip>
                                </Space>
                              )}
                            />
                          </Col>
                          <Col span={8} className="u-mb-8">
                            <Typography.Text strong>
                              {t('employee.grantDate')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="idCardIssuedAt"
                              control={method.control}
                              render={({ field, fieldState: { error } }) => (
                                <Picker
                                  className="u-mt-8"
                                  size="large"
                                  value={field.value ? dayjs(field.value) : undefined}
                                  onChange={field.onChange}
                                  format="DD/MM/YYYY"
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                  error={error?.message}
                                />
                              )}
                            />
                          </Col>
                          <Col span={8} className="u-mb-8">
                            <Typography.Text strong>
                              {t('employee.grantPlace')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="idCardIssuedBy"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Input
                                  className="u-mt-8"
                                  value={value}
                                  onChange={onChange}
                                  error={error?.message}
                                  size="large"
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                />
                              )}
                            />
                          </Col>
                          <Col span={8}>
                            <Typography.Text strong>
                              {t('employee.fullname')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="fullname"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Input
                                  className="u-mt-8"
                                  value={value}
                                  onChange={(e) => {
                                    onChange(e);
                                    method.getValues('bankAccounts').forEach((_, index) => {
                                      method.setValue(`bankAccounts.${index}.bankOwner`, e.target.value);
                                    });
                                  }}
                                  error={error?.message}
                                  size="large"
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                />
                              )}
                            />
                          </Col>
                          <Col span={8}>
                            <Typography.Text strong>
                              {t('employee.birthDate')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="dob"
                              control={method.control}
                              render={({ field, fieldState: { error } }) => (
                                <Picker
                                  className="u-mt-8"
                                  size="large"
                                  value={field.value ? dayjs(field.value) : undefined}
                                  onChange={field.onChange}
                                  format="DD/MM/YYYY"
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                  error={error?.message}
                                  disableDateUnder18age
                                />
                              )}
                            />
                          </Col>
                          <Col span={8}>
                            <Typography.Text strong>
                              {t('employee.taxNumber')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <div>
                              <Controller
                                name="taxCode"
                                control={method.control}
                                render={({
                                  field: { value, onChange },
                                  fieldState: { error },
                                }) => (
                                  <>
                                    <AutoComplete
                                      className="u-mt-8"
                                      style={{ width: '100%' }}
                                      value={value}
                                      onChange={onChange}
                                      options={[NoTaxCode]}
                                      size="large"
                                      disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                    >
                                      <Input
                                        value={value ?? undefined}
                                        onChange={onChange}
                                        size="large"
                                        disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                        allowClear
                                      />
                                    </AutoComplete>

                                    {error && (
                                      <span
                                        className="a-input_errorMessage"
                                      >
                                        {error.message}
                                      </span>
                                    )}
                                  </>
                                )}
                              />
                            </div>
                          </Col>
                          <Col span={12} className="u-mt-8">
                            <Typography.Text strong>
                              {t('system.phone')}
                            </Typography.Text>
                            {' '}
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="phoneNumber"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Input
                                  type="number"
                                  className="u-mt-8"
                                  value={value}
                                  onChange={onChange}
                                  error={error?.message}
                                  size="large"
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                />
                              )}
                            />
                          </Col>
                          <Col span={12} className="u-mt-8">
                            <Typography.Text strong>
                              {t('employee.address')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="permanentAddress"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Input
                                  className="u-mt-8"
                                  value={value}
                                  onChange={onChange}
                                  error={error?.message}
                                  size="large"
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                />
                              )}
                            />
                          </Col>
                          <Card
                            extra={canAccessOCR ? (
                              <Button
                                type="primary"
                                disabled={isLoadingInfoFromOCR || !frontIdentityPath || !backIdentityPath || !(isDirtyIdCardFrontImage || isDirtyIdCardBackImage)}
                                onClick={() => mutateInfoFromOCR()}
                              >
                                <ScanOutlined />
                                OCR
                              </Button>
                            ) : null}
                            className="u-mt-16"
                            style={{ width: '100%' }}
                          >
                            <Row gutter={16}>
                              <Col span={12} className="u-mt-8">
                                <Typography.Text strong>
                                  {t('employee.idCardFront')}
                                  {' '}
                                </Typography.Text>
                                <Typography.Text strong type="danger">
                                  *
                                </Typography.Text>
                                <Controller
                                  name="idCardFrontImage"
                                  render={({
                                    field: { value, onChange }, fieldState: { error }
                                  }) => (
                                    <>
                                      <UploadPhoto
                                        idUpload="idCardFrontImage"
                                        description="Upload hình ảnh"
                                        className="u-mt-8"
                                        // title="Mặt trước"
                                        handleChooseFile={onChange}
                                        handleRemoveFile={() => {
                                          onChange(undefined);
                                        }}
                                        value={value}
                                        big
                                        readOnly={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                      />
                                      {error && (
                                        <span
                                          className="a-input_errorMessage"
                                        >
                                          {error.message}
                                        </span>
                                      )}
                                    </>
                                  )}
                                />
                              </Col>
                              <Col span={12} className="u-mt-8">
                                <Typography.Text strong>
                                  {t('employee.idCardBack')}
                                  {' '}
                                </Typography.Text>
                                <Typography.Text strong type="danger">
                                  *
                                </Typography.Text>
                                <Controller
                                  name="idCardBackImage"
                                  render={({
                                    field: { value, onChange },
                                    fieldState: { error },
                                  }) => (
                                    <>
                                      <UploadPhoto
                                        idUpload="idCardBack"
                                        description="Upload hình ảnh"
                                        className="u-mt-8"
                                        // title="Mặt trước"
                                        handleChooseFile={onChange}
                                        handleRemoveFile={() => {
                                          onChange(undefined);
                                        }}
                                        value={value}
                                        big
                                        readOnly={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                      />
                                      {error && (
                                        <span
                                          className="a-input_errorMessage"
                                        >
                                          {error.message}
                                        </span>
                                      )}
                                    </>
                                  )}
                                />
                              </Col>
                            </Row>
                          </Card>
                        </Row>
                      </Card>
                      <Card title={<Typography.Title level={4}>{t('employee.contract')}</Typography.Title>} className="u-mt-16">
                        <Row gutter={16}>
                          <Col span={24} className="u-mt-8">
                            <Typography.Text strong>
                              {t('employee.contractType')}
                              {' '}
                            </Typography.Text>
                            <Typography.Text strong type="danger">
                              *
                            </Typography.Text>
                            <Controller
                              name="contractType"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <DropdownElement
                                  options={contractTypeOptions}
                                  locale="vi"
                                  placeholder={t('employee.contractType')}
                                  isGetOption
                                  value={value}
                                  isShowSearch
                                  error={error?.message}
                                  onChange={onChange}
                                  size="large"
                                  disabled
                                />
                              )}
                            />
                          </Col>
                        </Row>
                      </Card>
                      <Card title={<Typography.Title level={4}>{t('employee.socialInsurance')}</Typography.Title>} className="u-mt-16">
                        <Row gutter={16}>
                          <Col span={12}>
                            <Typography.Text strong>
                              {t('employee.socialInsuranceNumber')}
                              {' '}
                            </Typography.Text>
                            <Controller
                              name="socialInsuranceNumber"
                              control={method.control}
                              render={({
                                field: { value, onChange },
                                fieldState: { error },
                              }) => (
                                <Input
                                  className="u-mt-8"
                                  value={value}
                                  onChange={onChange}
                                  error={error?.message}
                                  size="large"
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                />
                              )}
                            />
                          </Col>
                          <Col span={12}>
                            <Typography.Text strong>
                              {t('employee.insuranceDate')}
                              {' '}
                            </Typography.Text>
                            <Controller
                              name="socialInsuranceJoinDate"
                              control={method.control}
                              render={({ field, fieldState: { error } }) => (
                                <Picker
                                  className="u-mt-8"
                                  value={field.value ? dayjs(field.value) : undefined}
                                  onChange={field.onChange}
                                  format="DD/MM/YYYY"
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                  error={error?.message}
                                  size="large"
                                />
                              )}
                            />
                          </Col>
                          <Col span={24} className="u-mt-8">
                            <Typography.Text strong>
                              {t('employee.vssidImg')}
                              {' '}
                            </Typography.Text>
                            <Controller
                              name="socialInsuranceImage"
                              render={({
                                field: { value, onChange }, fieldState: { error }
                              }) => (
                                <>
                                  <UploadPhoto
                                    idUpload="vssidImg"
                                    description="Upload hình ảnh"
                                    className="u-mt-8"
                                    // title="Mặt trước"
                                    handleChooseFile={onChange}
                                    handleRemoveFile={() => {
                                      onChange(undefined);
                                    }}
                                    value={value}
                                    readOnly={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                  />
                                  {error && (
                                    <span
                                      className="a-input_errorMessage"
                                    >
                                      {error.message}
                                    </span>
                                  )}
                                </>
                              )}
                            />
                          </Col>
                        </Row>
                      </Card>
                      <Card title={<Typography.Title level={4}>{t('employee.bank')}</Typography.Title>} className="u-mt-16">
                        {fields.map((item, index) => (
                          <Card
                            title={(
                              <Typography.Text>
                                {t('employee.bankAccountInfo')}
                                {' '}
                                {index + 1}
                              </Typography.Text>
                            )}
                            key={`employee-${item.id}`}
                            className="u-mt-16"
                            extra={(Boolean(idParams) && !roleUpdate) || !deleteBankRole ? null : (
                              <Space>
                                <Button
                                  type="text"
                                  onClick={(e) => {
                                    if (e.stopPropagation) e.stopPropagation();
                                    Modal.confirm({
                                      className: 't-pagetable_deleteLanguageModal',
                                      autoFocusButton: 'cancel',
                                      okText: t('system.ok'),
                                      cancelText: t('system.cancel'),
                                      cancelButtonProps: {
                                        type: 'primary',
                                      },
                                      okButtonProps: {
                                        type: 'default',
                                      },
                                      title: t('message.confirmDeleteRecord'),
                                      onOk: () => handleRemove(index),
                                    });
                                  }}
                                  icon={<DeleteOutlined />}
                                  disabled={viewOnly || (!hasCheckedIdCard && !idParams)}
                                />
                              </Space>
                            )}
                          >
                            <Row gutter={16}>
                              <Col span={8} className="u-mb-8">
                                <Typography.Text strong>
                                  {t('employee.fullname')}
                                  {' '}
                                </Typography.Text>
                                <Typography.Text strong>
                                  {' '}
                                </Typography.Text>
                                <Typography.Text strong type="danger">
                                  *
                                </Typography.Text>
                                <Controller
                                  name={`bankAccounts.${index}.bankOwner`}
                                  render={({
                                    field: { value, onChange },
                                    fieldState: { error },
                                  }) => (
                                    <Input
                                      className="u-mt-8"
                                      name="name"
                                      placeholder={t('system.name')}
                                      value={value}
                                      onChange={onChange}
                                      error={error?.message}
                                      size="large"
                                      disabled
                                    />
                                  )}
                                />
                              </Col>
                              <Col span={8} className="u-mb-8">
                                <Typography.Text strong>
                                  {t('employee.bankAccount')}
                                  {' '}
                                </Typography.Text>
                                <Typography.Text strong>
                                  {' '}
                                </Typography.Text>
                                <Typography.Text strong type="danger">
                                  *
                                </Typography.Text>
                                <Controller
                                  name={`bankAccounts.${index}.bankAccount`}
                                  render={({
                                    field: { value, onChange },
                                    fieldState: { error },
                                  }) => (
                                    <Input
                                      className="u-mt-8"
                                      name="name"
                                      placeholder={t('employee.bankAccount')}
                                      value={value}
                                      onChange={(e) => {
                                        onChange(e.target.value.toUpperCase());
                                      }}
                                      error={error?.message}
                                      size="large"
                                      disabled={method.getValues(`bankAccounts.${index}.isDisabled`) || viewOnly || (!hasCheckedIdCard && !idParams)}
                                    />
                                  )}
                                />
                              </Col>
                              <Col span={4} className="u-mb-8">
                                <Typography.Text strong>
                                  {t('employee.defaultAccount')}
                                  {' '}
                                </Typography.Text>
                                <Typography.Text strong>
                                  {' '}
                                </Typography.Text>
                                <Typography.Text strong type="danger">
                                  *
                                </Typography.Text>
                                <div className="u-mt-8">
                                  <Controller
                                    name={`bankAccounts.${index}.isDefault`}
                                    render={({
                                      field: { value, onChange },
                                      fieldState: { error },
                                    }) => (
                                      <>
                                        <Switch
                                          checked={value}
                                          onChange={(e) => {
                                            onChange(e);
                                            onToggleDefault(e, index);
                                          }}
                                          disabled={viewOnly || (!hasCheckedIdCard && !idParams) || (Boolean(idParams) && !roleUpdate)}
                                        />
                                        {error && (
                                          <span className="a-input_errorMessage">
                                            {error.message}
                                          </span>
                                        )}
                                      </>
                                    )}
                                  />
                                </div>
                              </Col>
                              <Col span={4} className="u-mb-8">
                                <Typography.Text strong>
                                  {t('employee.activate')}
                                  {' '}
                                </Typography.Text>
                                <Typography.Text strong>
                                  {' '}
                                </Typography.Text>
                                <div className="u-mt-8">
                                  <Controller
                                    name={`bankAccounts.${index}.isActive`}
                                    render={({
                                      field: { value, onChange },
                                      fieldState: { error },
                                    }) => (
                                      <>
                                        <Switch
                                          disabled
                                          checked={value}
                                          onChange={onChange}
                                        />
                                        {error && (
                                          <span className="a-input_errorMessage">
                                            {error.message}
                                          </span>
                                        )}
                                      </>
                                    )}
                                  />
                                </div>
                              </Col>
                              <Col span={8} className="u-mb-8">
                                <Typography.Text strong>
                                  {t('employee.bankName')}
                                  {' '}
                                </Typography.Text>
                                <Typography.Text strong type="danger">
                                  *
                                </Typography.Text>
                                <Controller
                                  name={`bankAccounts.${index}.bankName`}
                                  control={method.control}
                                  render={({
                                    field: { value, onChange },
                                    fieldState: { error },
                                  }) => (
                                    <DropdownElement
                                      locale="vi"
                                      type="bank"
                                      placeholder={t('employee.bankName')}
                                      isGetOption
                                      value={value}
                                      isValueSlug
                                      isShowSearch
                                      error={error?.message}
                                      onChange={(val) => {
                                        onChange(val);
                                        setWatchIndex(index);
                                        method.setValue(`bankAccounts.${index}.city`, null);
                                        method.setValue(`bankAccounts.${index}.bankBranch`, null);
                                        // method.setValue(`bankAccounts.${index}.bankCode`, '');
                                      }}
                                      size="large"
                                      slugs={initBankNames?.[index]}
                                      disabled={method.getValues(`bankAccounts.${index}.isDisabled`) || viewOnly || (!hasCheckedIdCard && !idParams)}
                                    />
                                  )}
                                />
                              </Col>
                              <Col span={8} className="u-mb-8">
                                <Typography.Text strong>
                                  {t('employee.city')}
                                  {' '}
                                </Typography.Text>
                                {disabledBankField(index) ? null : (
                                  <Typography.Text strong type="danger">
                                    *
                                  </Typography.Text>
                                )}
                                <Controller
                                  name={`bankAccounts.${index}.city`}
                                  control={method.control}
                                  render={({
                                    field: { value, onChange },
                                    fieldState: { error },
                                  }) => (
                                    <DropdownElement
                                      options={cityListOption}
                                      locale="vi"
                                      placeholder={t('employee.city')}
                                      isGetOption
                                      value={value}
                                      isShowSearch
                                      error={error?.message}
                                      onChange={(val) => {
                                        onChange(val);
                                        method.setValue(`bankAccounts.${index}.bankBranch`, null);
                                        // method.setValue(`bankAccounts.${index}.bankCode`, '');
                                      }}
                                      size="large"
                                      handleLoadMore={fetchMoreCityList}
                                      loading={isFetchingCity || getCityLoading}
                                      disabled={method.getValues(`bankAccounts.${index}.isDisabled`) || disabledBankField(index) || viewOnly || (!hasCheckedIdCard && !idParams)}
                                    />
                                  )}
                                />
                              </Col>
                              <Col span={8} className="u-mb-8">
                                <Typography.Text strong>
                                  {t('employee.bankBranch')}
                                  {' '}
                                </Typography.Text>
                                {disabledBankField(index) ? null : (
                                  <Typography.Text strong type="danger">
                                    *
                                  </Typography.Text>
                                )}
                                <Controller
                                  name={`bankAccounts.${index}.bankBranch`}
                                  control={method.control}
                                  render={({
                                    field: { value, onChange },
                                    fieldState: { error },
                                  }) => (
                                    <DropdownElement
                                      options={branchListOption}
                                      locale="vi"
                                      placeholder={t('employee.bankBranch')}
                                      isGetOption
                                      value={value}
                                      isShowSearch
                                      error={error?.message}
                                      onChange={(val) => {
                                        onChange(val);
                                        // method.setValue(`bankAccounts.${index}.bankCode`, val.value);
                                        method.setValue(`bankAccounts.${index}.bankId`, val.bankId);
                                      }}
                                      size="large"
                                      handleLoadMore={fetchMoreBranchList}
                                      loading={isFetchingBranch || getBranchLoading}
                                      disabled={method.getValues(`bankAccounts.${index}.isDisabled`) || disabledBankField(index) || viewOnly || (!hasCheckedIdCard && !idParams)}
                                    />
                                  )}
                                />
                              </Col>
                            </Row>
                          </Card>
                        ))}
                        {errorState.bankAccounts
                          && (
                            <span className="a-input_errorMessage">
                              {errorState.bankAccounts.message}
                            </span>
                          )}
                        {
                          ((!!idParams && roleUpdate) || (!idParams && roleCreate) || !!program) && (
                            <Button
                              className="u-mt-32"
                              style={{ display: 'block', marginLeft: 'auto' }}
                              type="primary"
                              onClick={() => {
                                if (!method.getValues('fullname')) {
                                  method.setError('bankAccounts', {
                                    type: 'custom',
                                    message: 'Vui lòng nhập Họ và Tên'
                                  });
                                } else {
                                  if (method.formState.errors.bankAccounts) {
                                    method.clearErrors('bankAccounts');
                                  }
                                  const currentAccounts = method.getValues('bankAccounts');
                                  currentAccounts.forEach((_, index) => {
                                    method.setValue(`bankAccounts.${index}.isDefault`, false);
                                  });

                                  append({
                                    bankOwner: removeVietnameseDiacriticsAndUppercase(method.getValues('fullname')),
                                    bankAccount: '',
                                    isDefault: true,
                                    isActive: true,
                                    bankName: null,
                                    bankBranch: null,
                                    city: null,
                                  });
                                }
                              }}
                              disabled={viewOnly || (!hasCheckedIdCard && !idParams)}
                            >
                              <PlusOutlined />
                              {t('employee.addAccount')}
                            </Button>
                          )
                        }
                      </Card>
                    </Space>
                  </div>
                </Card>
              </Col>
            </Row>
            {
              program && (
                <Space size={16} style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                  <Button
                    type="primary"
                    loading={program.loading}
                    disabled={roleUpdate || !hasCheckedIdCard}
                    onClick={() => {
                      if (isAddStaffProgram) {
                        const idCardNumber = method.getValues('idCardNumber');
                        program.handleClosePopup?.(idCardNumber);
                      } else {
                        method.handleSubmit(onSubmit)();
                      }
                    }}
                  >
                    <SaveOutlined />
                    {t('system.save')}
                  </Button>
                </Space>
              )
            }
          </FormProvider>
        </div>
      </Spin>
      <Modal title={t('employee.checkExistedTitle')} open={!!existedInfo} onOk={() => onReuseInfo()} onCancel={() => setExistedInfo(null)}>
        <div dangerouslySetInnerHTML={{
          __html: existedInfo?.info ? (
            `${t('employee.identityNumber')} <strong>${existedInfo?.idNumber}</strong> ${renderReuseMessage}.`
          ) : (
            `${t('employee.identityNumber')} <strong>${existedInfo?.idNumber}</strong> ${renderReuseMessage}.`
          )
        }}
        />
      </Modal>
      <Modal
        title={t('employee.idCardNotMatchTitle')}
        open={isNotMatchIdCardNumber}
        onOk={() => {
          setIsNotMatchIdCardNumber(false);
          if (data) {
            const {
              backIdCard, backIdCardPath, frontIdCard, frontIdCardPath
            } = data.staffData;
            method.reset({
              ...method.getValues(),
              idCardFrontPath: frontIdCardPath || '',
              idCardFrontImage: {
                file: undefined,
                pathSubmit: frontIdCard || '',
              },
              idCardBackPath: backIdCardPath || '',
              idCardBackImage: {
                file: undefined,
                pathSubmit: backIdCard || '',
              },
            });
          }
        }}
        cancelButtonProps={{ style: { display: 'none' } }}
        okText={t('system.ok')}
      >
        <div dangerouslySetInnerHTML={{
          __html: t('employee.idCardNotMatch')
        }}
        />
      </Modal>
    </>
  );
});

export default CreateEmployee;
